// Up next on a customer's page (Liam, 28-29 Oct 2026, design C1 v3: https://claude.ai/artifact/UADktoaCaNFj5WZqSDH5UD;
// "C is easier to follow at a glance", "I like C1", "the done tick is better in C3", "build it get it shipped and live").
// What this customer needs from the owner, in order, as numbered steps down a line (the style kit's stops on a route):
// money first, then Book again or making them a regular, asking about their next clean, and missing details last. Each
// step carries the page's own card or button for it, so nothing here saves anything of its own. A step that leaves (its
// action saved) moves into "Done" with the kit's tick; the last one leaving shows the kit's finish mark and "Nothing
// needs you for <name>". docs/plans/customer-next-steps/ (Sonnet's and GPT-6.1's lists) is what may join later.
import { el } from './ui'
import { lineDrawing } from './picture-tiles'
import { buzz } from './haptic'

export interface UpNextStep {
  id: string // stable for this customer, so a step that leaves is recognised as done
  title: string
  line?: string
  warn?: boolean // the line in the needs colour (money waiting)
  money?: string
  body?: HTMLElement | null // the page's own card or button for this step
  waits?: boolean // shown, not the next thing to do (after their payment)
}

// What each customer's page showed last time it was drawn in this visit to the app, to tell what just got done.
const shown = new Map<number, { ids: string[]; titles: Map<string, string>; done: { id: string; title: string }[] }>()

const quiet = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

// unknown: steps whose read failed this time; they are kept as they were, never counted as done.
export function upNextSection(customerId: number, firstName: string, steps: UpNextStep[], unknown: string[] = []): HTMLElement | null {
  const before = shown.get(customerId)
  const ids = [...steps.map((s) => s.id), ...unknown.filter((u) => before?.ids.includes(u))]
  const titles = new Map(steps.map((s) => [s.id, s.title]))
  const justDone = before ? before.ids.filter((id) => !ids.includes(id)).map((id) => ({ id, title: before.titles.get(id) ?? '' })) : []
  const done = [...justDone, ...(before?.done ?? []).filter((d) => !ids.includes(d.id) && !justDone.some((j) => j.id === d.id))]
  shown.set(customerId, { ids, titles, done })
  // A step whose read failed is still unknown: never "All done" or the finish mark while one is (Astra, 29 Oct 2026).
  const stillUnknown = ids.length > steps.length
  const finishedNow = !!before && before.ids.length > 0 && steps.length === 0 && !stillUnknown
  // The kit's haptics: a light tap for a tick, a success when the pile is finished.
  if (justDone.length) buzz(finishedNow ? 'success' : 'light')
  if (!steps.length && !done.length && !stillUnknown) return null

  const box = el('section', { class: 'up-next', 'aria-label': 'Up next' })
  box.append(el('div', { class: 'up-next-head' }, el('h2', {}, 'Up next'),
    el('span', {}, steps.length ? `${steps.length} to do` : stillUnknown ? '' : 'All done')))
  if (steps.length) {
    const line = el('ol', { class: 'up-next-steps' })
    let n = 0
    const firstLive = steps.findIndex((s) => !s.waits)
    steps.forEach((s, i) => {
      n += 1
      const ring = el('span', { class: `up-next-ring${i === firstLive ? ' is-now' : ''}${i === firstLive && justDone.length && !quiet() ? ' up-next-glow' : ''}`, 'aria-hidden': 'true' }, String(n))
      const card = el('div', { class: `up-next-card${s.waits ? ' is-waiting' : ''}` },
        el('div', { class: 'up-next-top' }, el('strong', {}, s.title), s.money ? el('strong', { class: 'up-next-money' }, s.money) : null),
        s.line ? el('span', { class: `up-next-line${s.warn ? ' is-warn' : ''}` }, s.line) : null)
      if (s.body) card.append(s.body)
      line.append(el('li', { class: 'up-next-step', 'data-step': s.id }, ring, card))
    })
    box.append(line)
  } else if (stillUnknown) {
    box.append(el('p', { class: 'up-next-clear' }, "Couldn't load everything for them. Pull down to try again."))
  } else if (finishedNow) {
    box.append(finishMark(firstName))
  } else {
    box.append(el('p', { class: 'up-next-clear' }, `Nothing needs you for ${firstName}.`))
  }
  if (done.length) {
    const list = el('div', { class: 'up-next-done' }, el('div', { class: 'up-next-done-head' }, 'Done'))
    for (const d of done) {
      const fresh = justDone.some((j) => j.id === d.id) && !quiet()
      list.append(el('div', { class: `up-next-done-row${fresh ? ' up-next-slide' : ''}` },
        el('span', { class: `up-next-tick${fresh ? ' up-next-pop' : ''}`, 'aria-hidden': 'true' }, lineDrawing('tick', 15)), el('span', {}, d.title)))
    }
    box.append(list)
  }
  return box
}

// The kit's one mark (Day done, All caught up): the big lemon circle with a slate tick, a small burst, and the words.
function finishMark(firstName: string): HTMLElement {
  const mark = el('div', { class: 'up-next-finish' })
  const ring = el('span', { class: 'up-next-mark', 'aria-hidden': 'true' }, lineDrawing('tick', 34))
  const dots = el('span', { class: 'up-next-dots', 'aria-hidden': 'true' })
  for (const [dx, dy, green] of [[-46, -30, false], [44, -34, true], [48, 26, false], [-42, 32, true]] as const) {
    dots.append(el('i', { style: `--dx:${dx}px;--dy:${dy}px`, class: green ? 'is-green' : '' }))
  }
  mark.append(el('span', { class: 'up-next-mark-wrap' }, ring, dots), el('strong', {}, `Nothing needs you for ${firstName}`))
  return mark
}
